import type { MouseEvent, KeyboardEvent } from 'react';
import React from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';

import ShowMoreIcon from '@atlaskit/icon/core/show-more-horizontal';
import Blanket from '@atlaskit/blanket';
import { IconButton } from '@atlaskit/button/new';
// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import Popup from '@atlaskit/popup';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { LoadableLazy } from '@confluence/loadable';

const LazySpaceMoreActionsMenuContent = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-SpaceMoreActionsMenuContent" */ './SpaceMoreActionsMenuContent.tsx'
			)
		).SpaceMoreActionsMenuContent,
});

const LazyRestrictedSpaceMoreActionsMenuContent = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-SpaceMoreActionsMenuContent" */ './SpaceMoreActionsMenuContent.tsx'
			)
		).RestrictedSpaceMoreActionsMenuContent,
});

type SpaceMoreActionsMenuProps = {
	spaceId: string;
	spaceKey: string;
	isStarred: boolean;
	isWatched: boolean;
	isSpaceAdmin: boolean;
	canManageSomeUsers: boolean;
	isOpen: boolean;
	setIsOpen: (isOpen: boolean) => void;
	isSpaceArchived?: boolean;
	hasDeleteSpacePermission?: boolean;
	hasArchiveSpacePermission?: boolean;
};

export const SpaceMoreActionsMenu = (props: SpaceMoreActionsMenuProps) => {
	const { isLoggedIn } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onClick = () => {
		props.setIsOpen(!props.isOpen);
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'spaceMoreActionsMenu',
				source: 'spaceNavigation',
				attributes: {
					navigationLayer: 'container',
					navigationContainer: 'sidebar',
					navVersion: '4',
					componentType: 'fixedItemButton',
					itemId: 'meatballDropdown',
					isSpaceAdmin: props.isSpaceAdmin,
				},
			},
		}).fire();
	};

	const onMenuItemClicked =
		(selection: string) => (e: KeyboardEvent<HTMLElement> | MouseEvent<HTMLElement>) => {
			e.stopPropagation();
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'spaceMoreActionsMenuItem',
					source: 'spaceMoreActionsMenu',
					attributes: {
						navigationLayer: 'container',
						navigationContainer: 'sidebar',
						navVersion: '4',
						componentType: 'fixedItemAction',
						itemId: selection,
						isSpaceAdmin: props.isSpaceAdmin,
					},
				},
			}).fire();
			props.setIsOpen(false);
		};

	return (
		// TODO: Add `shouldRenderToParent={true}` to meet a11y requirements. See
		// https://a11y.atlassian.net/browse/JCATS-788
		// https://hello.jira.atlassian.cloud/browse/DISCO-4258
		<Popup
			isOpen={props.isOpen}
			onClose={() => props.setIsOpen(false)}
			content={() => {
				return isLoggedIn ? (
					<LazySpaceMoreActionsMenuContent {...props} onMenuItemClicked={onMenuItemClicked} />
				) : (
					<LazyRestrictedSpaceMoreActionsMenuContent
						{...props}
						onMenuItemClicked={onMenuItemClicked}
					/>
				);
			}}
			trigger={(triggerProps) => (
				<>
					<IconButton
						{...triggerProps}
						icon={(iconProps) => <ShowMoreIcon {...iconProps} size="small" />}
						label={<FormattedMessage {...i18n.moreActions} />}
						onClick={onClick}
						spacing="compact"
						appearance="subtle"
						isSelected={props.isOpen}
					/>
					{props.isOpen ? (
						<Blanket
							testId="space-more-actions-menu-blanket"
							isTinted={false}
							shouldAllowClickThrough={!props.isOpen}
							onBlanketClicked={() => props.setIsOpen(false)}
						/>
					) : null}
				</>
			)}
			placement="right-start"
		/>
	);
};

const i18n = defineMessages({
	moreActions: {
		id: 'side-navigation.space-navigation.more-actions',
		defaultMessage: 'More actions',
		description:
			'Accessible label text for the ... button which opens a menu of additional actions for a space. Appears next to the space title.',
	},
});
